import { useMemo, useState } from 'react';
import styled from 'styled-components';

import CancelReferralModal from 'components/modals/referral/CancelReferralModal';
import StatusUpdateModal from 'components/modals/referral/StatusUpdateModal';
import AccessGuard from 'components/shared/AccessGuard';
import CircularProgress from 'components/shared/CircularProgress';
import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import useWebsocketExport from 'hooks/useWebsocketExport';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS } from 'models/Profile';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { createReviewExport } from 'services/api/reviews';
import colors from 'styles/theme/colors';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';
import CrissCross from 'svg/CrissCross';
import DownloadIcon from 'svg/DownloadIcon';
import EllipsisIcon from 'svg/EllipsisIcon';

type ProposedStatus = {
  status: ReviewStatus;
  label: string;
};

export const proposedStatusMap: Record<string, ProposedStatus> = {
  [ReviewStatus.ACCEPTED]: { status: ReviewStatus.REJECTED, label: 'Denied' },
  [ReviewStatus.REJECTED]: { status: ReviewStatus.ACCEPTED, label: 'Accepted' },
};

type Props = {
  referral: Referral;
  locationEpisode: LocationEpisode;
  episode: Episode;
};

export function ReferralMenu({ referral, locationEpisode, episode }: Props) {
  const { exporting, initiateExport } = useWebsocketExport(createReviewExport);
  const [showCancelReferral, setShowCancelReferral] = useState(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const { profile } = useProfile();

  const referralId = referral.id;

  const canCancelReferral = referral.locationEpisodeId === episode.latestLocationEpisodeId && !referral.canceled;

  const referralDisplayStatus = referral.statusLabel;
  const referredBy = useMemo(() => locationEpisode?.group, [locationEpisode]);

  const showUpdateStatusOption =
    referredBy &&
    referral?.groupId &&
    profile?.canAccessGroup(referral.groupId) &&
    (referral.accepted || referral.rejected) &&
    locationEpisode.latestReferralReview?.id === referral.id;

  return (
    <>
      <Container>
        <Menu>
          <Menu.Trigger>
            {exporting ? (
              <ExportLoadingIndicator thickness={2} />
            ) : (
              <MenuButton>
                <EllipsisIcon />
              </MenuButton>
            )}
          </Menu.Trigger>
          <Menu.Content position='right'>
            <Menu.Item onClick={() => initiateExport(referralId)}>
              <DownloadIcon width={14} height={14} />
              Download referral history
            </Menu.Item>
            {canCancelReferral && (
              <AccessGuard
                accessor={PERMISSIONS.reviewReferralCreate}
                locationEpisode={locationEpisode}
                episode={episode}>
                <Menu.Item
                  onClick={() => setShowCancelReferral(true)}
                  style={{ color: colors.accentRed }}
                  data-cy='cancelPendingReferral'>
                  <CircleXIconOutlined width={14} height={14} />
                  Cancel {referralDisplayStatus.toLowerCase()} referral
                </Menu.Item>
              </AccessGuard>
            )}
            {showUpdateStatusOption && (
              <AccessGuard
                accessor={PERMISSIONS.reviewReferralEdit}
                locationEpisode={locationEpisode}
                episode={episode}>
                <Menu.Item onClick={() => setShowStatusUpdateModal(true)}>
                  <CrissCross width={14} height={14} />
                  Change status to {proposedStatusMap[referral.status].label}
                </Menu.Item>
              </AccessGuard>
            )}
          </Menu.Content>
        </Menu>
      </Container>

      {showCancelReferral && (
        <CancelReferralModal setShow={setShowCancelReferral} locationEpisode={locationEpisode} referral={referral} />
      )}
      {showStatusUpdateModal && (
        <StatusUpdateModal setShow={setShowStatusUpdateModal} locationEpisode={locationEpisode} referral={referral} />
      )}
    </>
  );
}

const Container = styled.div`
  margin-left: auto;
  width: 20px;
  position: relative;
`;

const ExportLoadingIndicator = styled(CircularProgress)`
  width: 18px;
  height: 18px;
  color: ${colors.primaryBlue};
`;

const MenuButton = styled.button`
  all: unset;
  cursor: pointer;
  height: 16px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
`;
